import { ReactNode, useEffect, useRef } from "react";
import { Header } from "./Header";
import { MobileNav } from "./MobileNav";
import { useAuth } from "@/hooks/useAuth";
import { useUserCommunity } from "@/hooks/useUserCommunity";
import { HelpWidget } from "@/components/common/HelpWidget";
import { SystemStatusBanner } from "@/components/common/SystemStatusBanner";

interface AppLayoutProps {
  children: ReactNode;
  showNav?: boolean;
}

export function AppLayout({ children, showNav = true }: AppLayoutProps) {
  const { user, isSuperAdmin, isBusinessAdmin, isStaff } = useAuth();
  const { data: userCommunity } = useUserCommunity();

  // Determine if we should show community branding (customers only)
  const showCommunityBranding = !!userCommunity && !isSuperAdmin && !isBusinessAdmin && !isStaff;

  // Dynamically set favicon when customer is in their community
  useEffect(() => {
    if (showCommunityBranding && userCommunity?.favicon_url) {
      const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (link) {
        link.href = userCommunity.favicon_url;
      }
    }
  }, [showCommunityBranding, userCommunity?.favicon_url]);

  // Update document title with community name for customers
  const previousTitleRef = useRef(document.title);
  useEffect(() => {
    const newTitle = showCommunityBranding && userCommunity?.name
      ? userCommunity.name
      : "Business Pal";
    
    if (document.title !== newTitle) {
      document.title = newTitle;
    }

    return () => {
      document.title = previousTitleRef.current;
    };
  }, [showCommunityBranding, userCommunity?.name]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SystemStatusBanner />
      <Header />
      <main className="pb-24 lg:pb-0">{children}</main>
      {showNav && user && <MobileNav />}
      <HelpWidget />
    </div>
  );
}
